import { ModelName } from '@casops/store/api';
import {
  ConfigPackageFileType,
  ConfigPackageRequest,
  useDeleteConfigurationGroupPackageMutation,
} from '@casops/store/apis/configurationGroupsApi';
import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import { ColDef } from 'ag-grid-community';
import { ConfigPackageFileTypeFormatter } from '@casops/components/tables/formatters/enumFormatters';
import { Permission, useIsUserWithPermission } from '@casops/permissions';
import { columnDefModelActions } from '@casops/components/tables/grid/columnDefs';
import { ModelActionsCellRendererParams } from '@casops/components/tables/cells/model-actions/ModelActionsCellRenderer';
import { ConfigPackageListModel } from './ConfigurationGroupPackages';
import { useAbsoluteTimePartialColDef } from '@casops/components/tables/cells/absolute-time/useAbsoluteTime';

type Model = ConfigPackageListModel;

const modelName = ModelName.ConfigurationGroupPackage;
const permission = Permission.Admin;

const getItemName = (data: Model): string => data.name;

const getQueryArg = ({ id, configGroupId }: Model): ConfigPackageRequest => ({
  id,
  configGroupId,
});

// Only show the download action for 'Settings' packages.
const showDownloadItem = ({ ty }: Model): boolean => ty === ConfigPackageFileType.Settings;

// Only enable download if the upload is complete.
const canDownloadItem = ({ uc }: Model): boolean => uc;

const getDownloadUrl = ({ configGroupId, id }: Model): string =>
  `api/configurationgroups/${configGroupId}/packages/${id}/contents`;

const cellRendererParams: ModelActionsCellRendererParams<Model, ConfigPackageRequest> = {
  modelName,
  permission,
  downloadProps: {
    showDownloadItem,
    canDownloadItem,
    getDownloadUrl,
  },
  deleteProps: {
    canDeleteItem: true,
    getQueryArg,
    useDeleteModelMutation: useDeleteConfigurationGroupPackageMutation,
    getItemName,
  },
};

export const useConfigPackageColumnDefs = (): ColDef<Model>[] => {
  const { t } = useTranslation();

  const isAllowedModelActions = useIsUserWithPermission(permission);
  const absoluteTimeColDef = useAbsoluteTimePartialColDef<Model>();

  const columnDefs = useMemo<ColDef<Model>[]>(() => {
    const columnDefs: ColDef<Model>[] = [
      { field: 'name', headerName: t('Fields.PackageName'), minWidth: 170 },
      {
        field: 'ty',
        headerName: t('Fields.PackageType'),
        valueFormatter: ConfigPackageFileTypeFormatter<Model, 'ty'>,
        getQuickFilterText: ConfigPackageFileTypeFormatter<Model, 'ty'>,
        minWidth: 120,
      },
      { field: 'ver', headerName: t('Fields.Version'), minWidth: 80 },
      {
        colId: 'ActivationTime',
        field: 'at',
        headerName: t('Fields.ActivationTime'),
        sort: 'desc', // Sorts by the original timestamp, not the localised string.
        minWidth: 180,
        ...absoluteTimeColDef,
      },
    ];

    if (isAllowedModelActions) {
      columnDefs.push({
        ...columnDefModelActions<Model, ConfigPackageRequest>(t, cellRendererParams),
        minWidth: 80,
        maxWidth: 120,
      });
    }

    return columnDefs;
  }, [t, isAllowedModelActions, absoluteTimeColDef]);

  return columnDefs;
};

export default useConfigPackageColumnDefs;
